import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { factorService } from '../../services/api';

const SecondaryFactorsStep = ({ project, factors, drivers, scenarios, onDataChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [pendingAssignments, setPendingAssignments] = useState({}); // Temporary state for pending saves

  const secondaryFactors = factors.filter(f => !f.is_driver);
  const selectedScenarios = scenarios.filter(s => s.is_selected);

  // Get current assignment from factor data, with pending assignment override
  const getFactorAssignment = (factorId) => {
    // If there's a pending assignment, use that instead of server data
    if (pendingAssignments[factorId]) {
      return pendingAssignments[factorId];
    }
    
    const factor = factors.find(f => f.id === factorId);
    if (factor?.secondary_factor_assignment) {
      return {
        factor_type: factor.secondary_factor_assignment.factor_type || 'constant',
        driver_ids: factor.secondary_factor_assignment.driver_ids || []
      };
    }
    return {
      factor_type: 'constant',
      driver_ids: []
    };
  };

  const getTranslatedCategory = (category) => {
    const categoryMap = {
      'political': t('factors.pestel.political'),
      'economic': t('factors.pestel.economic'),
      'social': t('factors.pestel.social'),
      'technological': t('factors.pestel.technological'),
      'environmental': t('factors.pestel.environmental'),
      'legal': t('factors.pestel.legal'),
      'strengths': t('factors.swot.strengths'),
      'weaknesses': t('factors.swot.weaknesses'),
      'opportunities': t('factors.swot.opportunities'),
      'threats': t('factors.swot.threats'),
      'identify': t('factors.cyber.identify'),
      'protect': t('factors.cyber.protect'),
      'detect': t('factors.cyber.detect'),
      'respond': t('factors.cyber.respond'),
      'recover': t('factors.cyber.recover')
    };
    
    return categoryMap[category] || category;
  };

  const handleAssignmentChange = async (factorId, field, value) => {
    const currentAssignment = getFactorAssignment(factorId);
    const newAssignment = {
      ...currentAssignment,
      [field]: value
    };
    
    // If changing to constant, clear driver_ids
    if (field === 'factor_type' && value === 'constant') {
      newAssignment.driver_ids = [];
    }

    // Set pending assignment immediately for UI responsiveness
    setPendingAssignments(prev => ({
      ...prev,
      [factorId]: newAssignment
    }));

    // Auto-save the assignment
    await saveAssignment(factorId, newAssignment);
  };

  const handleDriverSelection = async (factorId, driverId, selected) => {
    const currentAssignment = getFactorAssignment(factorId);
    const currentDrivers = currentAssignment.driver_ids || [];
    
    const newDrivers = selected
      ? [...currentDrivers, driverId]
      : currentDrivers.filter(id => id !== driverId);

    const newAssignment = {
      ...currentAssignment,
      driver_ids: newDrivers
    };

    // Set pending assignment immediately for UI responsiveness
    setPendingAssignments(prev => ({
      ...prev,
      [factorId]: newAssignment
    }));

    // Auto-save the assignment
    await saveAssignment(factorId, newAssignment);
  };

  const saveAssignment = async (factorId, assignment) => {
    if (!assignment.factor_type) return;

    const assignmentData = {
      causal_factor_id: parseInt(factorId),
      factor_type: assignment.factor_type || 'constant',
      driver_ids: assignment.driver_ids || []
    };

    try {
      setLoading(true);
      
      // Check if this is a case or project context
      if (project.case_type) {
        // It's a case (has case_type property)
        await factorService.assignCaseSecondaryFactors(project.id, [assignmentData]);
      } else {
        // It's a project/workspace
        await factorService.assignSecondaryFactors(project.id, [assignmentData]);
      }
      
      onDataChange(); // Call onDataChange to refresh the data
      
      // Clear pending assignment after successful save and data refresh
      setTimeout(() => {
        setPendingAssignments(prev => {
          const updated = { ...prev };
          delete updated[factorId];
          return updated;
        });
      }, 100); // Small delay to ensure onDataChange completes
    } catch (error) {
      console.error('Error assigning secondary factor:', error); // Debug log
      toast.error(t('secondaryFactors.assignError'));
    } finally {
      setLoading(false);
    }
  };

  if (secondaryFactors.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto bg-success-light rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-success-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-neutral-900 mb-2">
          {t('secondaryFactors.allDrivers')}
        </h3>
        <p className="text-neutral-600">
          {t('secondaryFactors.allDriversDescription')}
        </p>
      </div>
    );
  }

  if (selectedScenarios.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-neutral-900 mb-2">
          {t('secondaryFactors.noScenariosSelected')}
        </h3>
        <p className="text-neutral-600">
          {t('secondaryFactors.noScenariosDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Compact Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-neutral-900">{t('secondaryFactors.title')}</h3>
            <button
              onClick={() => setShowHelpModal(true)}
              className="w-5 h-5 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center text-neutral-500 hover:text-neutral-700 transition-colors"
              title={t('secondaryFactors.showHelp')}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-600">
              {t('secondaryFactors.factorsStatus', { count: secondaryFactors.length })}
            </span>
            {secondaryFactors.some(f => f.secondary_factor_assignment) && (
              <div className="text-xs text-neutral-500 bg-accent-100 px-2 py-1 rounded">
                {t('secondaryFactors.assignedCount', { count: secondaryFactors.filter(f => f.secondary_factor_assignment).length })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Factors Table */}
      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/4">
                  {t('secondaryFactors.factor')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider w-32">
                  {t('secondaryFactors.behavior')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  {t('secondaryFactors.followsDrivers')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {secondaryFactors.map(factor => {
                const assignment = getFactorAssignment(factor.id);
                const selectedDrivers = assignment.driver_ids || [];
                const isFollowsDriver = assignment.factor_type === 'follows_driver';
                
                return (
                  <tr key={factor.id} className="hover:bg-neutral-50">
                    {/* Factor Info */}
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-neutral-200 rounded-full flex items-center justify-center text-sm font-medium text-neutral-600 mr-3">
                          {factor.name.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-neutral-900">{factor.name}</div>
                          <div className="text-sm text-neutral-500">{getTranslatedCategory(factor.category)}</div>
                        </div>
                      </div>
                    </td>

                    {/* Behavior Type */}
                    <td className="px-6 py-4 text-center">
                      <select
                        value={assignment.factor_type || 'constant'}
                        onChange={(e) => handleAssignmentChange(factor.id, 'factor_type', e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                      >
                        <option value="constant">{t('secondaryFactors.constant')}</option>
                        <option value="follows_driver">{t('secondaryFactors.followsDriver')}</option>
                      </select>
                    </td>

                    {/* Drivers Selection */}
                    <td className="px-6 py-4">
                      {isFollowsDriver ? (
                        <div className="flex flex-wrap gap-2">
                          {drivers.map(driver => (
                            <label key={driver.id} className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedDrivers.includes(driver.id)}
                                onChange={(e) => handleDriverSelection(factor.id, driver.id, e.target.checked)}
                                className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-neutral-300 rounded"
                              />
                              <span className="ml-2 text-sm text-neutral-700">
                                {driver.causal_factor_name}
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-neutral-400 italic">
                          {t('secondaryFactors.noDriversNeeded')}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="p-6 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-neutral-900">
                  {t('secondaryFactors.helpTitle')}
                </h3>
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="prose prose-sm max-w-none text-neutral-700">
                <p className="mb-4">
                  {t('secondaryFactors.stepIntroduction')}
                </p>
                
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-neutral-900 mb-3">{t('secondaryFactors.behaviorTypes')}:</h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm font-medium text-neutral-700">{t('secondaryFactors.constant')}</p>
                        <p className="text-sm text-neutral-600">{t('secondaryFactors.constantDescription')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm font-medium text-neutral-700">{t('secondaryFactors.followsDriver')}</p>
                        <p className="text-sm text-neutral-600">{t('secondaryFactors.followsDriverDescription')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
              <button
                onClick={() => setShowHelpModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecondaryFactorsStep;